/**
 * WCC360 v2 — Skills Intelligence™ inside the assessment.
 *
 * "Skills that carry you forward" used to be an average of the *careers* a
 * learner matched with, which meant the chips described the jobs, not the
 * person. This module pulls the Skills Intelligence™ evidence bank (Layer 2)
 * into the walk itself, so those chips rest on something the learner actually
 * did.
 *
 * Rules kept intact from the bank:
 *  · No Likert, no virtue self-ratings. Only "have you done / used / shipped".
 *  · The client never defines items — every item id and option id here comes
 *    from the frozen `EVIDENCE_ITEMS` bank.
 *  · Nothing here is an eligibility filter. Missing evidence = no claim, never
 *    a penalty, and "nothing here yet" is a real, honoured answer.
 *
 * Answers are recorded like any other Part 2 screen (one row per screen in
 * `assessment_v2_responses`, replayable on any device), and are also pushed to
 * the Skills Engine's own store at the end of the run so the living dashboard
 * and the report agree.
 */
import { EVIDENCE_ITEMS, computeSkills } from "@wcc360/engine/skills-engine";
import type {
  EvidenceItem, EvidenceRecord, InferredSkill,
} from "@wcc360/engine/skills-engine";
import type { Band } from "./band";
import type { AssessmentV2State } from "./types";

/** Screen id → the item ids that screen asks. Ordered, broad, not exhaustive. */
export const SKILLS_SCREEN_ITEMS: Record<string, string[]> = {
  px_skills_made: ["SI-01", "SI-17", "SI-21"],
  px_skills_tools: ["SI-02", "SI-06", "SI-11"],
  px_skills_carried: ["SI-22", "SI-27", "SI-33"],
  px_skills_learning: ["SI-35", "SI-37", "SI-03"],
};

export const SKILLS_SCREEN_IDS = Object.keys(SKILLS_SCREEN_ITEMS);

/**
 * The 12–14 band gets two screens, not four: a twelve-year-old has no paid
 * work and no team to have led, and asking would only teach them they're
 * behind. What they have made, and how they learn, are both fair questions.
 */
const SCREENS_12_14 = ["px_skills_made", "px_skills_learning"];

export function skillsScreensFor(band: Band): string[] {
  return band === "12to14" ? SCREENS_12_14 : SKILLS_SCREEN_IDS;
}

export const SKILLS_SCREEN_COPY: Record<string, { title: string; subtitle: string }> = {
  px_skills_made: {
    title: "What have you actually made?",
    subtitle:
      "Anything you started counts, finished or not. Nothing here is a test — it's the evidence your strengths get built from.",
  },
  px_skills_tools: {
    title: "What have you used yourself?",
    subtitle:
      "Used, not watched. Two afternoons of poking at something is enough to tick it.",
  },
  px_skills_carried: {
    title: "What have you carried for other people?",
    subtitle:
      "Work, teams, money, an audience. Small and unofficial still counts.",
  },
  px_skills_learning: {
    title: "Who's depended on you, and how do you learn?",
    subtitle:
      "The last two. These tell us how quickly new work becomes yours.",
  },
};

const ITEM_INDEX = new Map(EVIDENCE_ITEMS.map((i) => [i.item_id, i]));

/** The items one screen asks, resolved from the frozen bank. */
export function itemsForScreen(screenId: string): EvidenceItem[] {
  return (SKILLS_SCREEN_ITEMS[screenId] ?? [])
    .map((id) => ITEM_INDEX.get(id))
    .filter((i): i is EvidenceItem => Boolean(i));
}

/** What one skills screen stores: item id → chosen option ids. */
export type SkillsScreenAnswer = Record<string, string[]>;

function asAnswer(value: unknown): SkillsScreenAnswer {
  if (!value || typeof value !== "object") return {};
  const out: SkillsScreenAnswer = {};
  for (const [itemId, ids] of Object.entries(value as Record<string, unknown>)) {
    if (!ITEM_INDEX.has(itemId) || !Array.isArray(ids)) continue;
    const allowed = new Set(ITEM_INDEX.get(itemId)!.options.map((o) => o.option_id));
    out[itemId] = ids.filter((id): id is string => typeof id === "string" && allowed.has(id));
  }
  return out;
}

/**
 * Every skills answer in a run, as engine `EvidenceRecord`s. Sanitised against
 * the bank on the way out, so a stale or hand-edited local run can never
 * inject an item the engine doesn't know.
 */
export function skillsEvidenceFrom(
  state: AssessmentV2State | null,
  occurredAt = new Date().toISOString(),
): EvidenceRecord[] {
  if (!state) return [];
  const records: EvidenceRecord[] = [];
  for (const screenId of SKILLS_SCREEN_IDS) {
    const answer = asAnswer(state.part2?.[screenId]);
    for (const [item_id, option_ids] of Object.entries(answer)) {
      if (option_ids.length === 0) continue;
      records.push({ item_id, option_ids, source: "assessment", occurred_at: occurredAt });
    }
  }
  return records;
}

/** Inferred skills for a run — strongest first. Empty when nothing was ticked. */
export function inferredSkillsFrom(state: AssessmentV2State | null): InferredSkill[] {
  const records = skillsEvidenceFrom(state);
  if (records.length === 0) return [];
  return [...computeSkills(records).skills].sort(
    (a, b) => b.level_score - a.level_score || (a.label < b.label ? -1 : 1),
  );
}

const LEVEL_SENTENCE: Record<InferredSkill["level"], string> = {
  aware: "You've been near this — worth one small project to make it yours",
  novice: "You've started this, and it already shows",
  intermediate: "You've done this for real, more than once",
  advanced: "You've done this end-to-end, and can show it",
  expert: "You've done this under pressure, for someone else",
};

/** One warm, traceable sentence per skill — never a score on its own. */
export function skillSentence(s: InferredSkill): string {
  const evidence = s.evidence.slice(0, 2).join("; ");
  return evidence
    ? `${LEVEL_SENTENCE[s.level]}. From what you told us: ${evidence.toLowerCase()}.`
    : `${LEVEL_SENTENCE[s.level]}.`;
}
